
import React, { useCallback, useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { FieldStateKey, NON_ASSIGNABLE_VALUE, PermissionKey,REQUIRED_TEXT } from '@/common/constants';
import { AccountToPushToField } from '@/components/elements/AccountToPushToField';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { BankInfo } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';
import { BankMethod } from '@/modules/employer/hooks/useBankMethods.query';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

import { AccountMethods,AccountUsageValues } from '../AccountUsage.types';

const placeholder = 'Select bank account';
const defaultHoldPeriod = 3;
const defaultGiveUpDays = 30;
const ACCOUNT_PLACEHOLDER = 'The account used to push funds for balance adjustment will be provided once organization is activated.';
  
const useBalanceAdjustmentFields = (
  banks: BankInfo[],
  methods: BankMethod[],
  formattedData: EmployerDto,
  currentValue?: AccountUsageValues,
  activateMode?: boolean,
  isLoading?: boolean,
  hsaClearingAccount?: string,
  routingNumber?: string,
) => {
  const externalBalanceAdjustmentAccountMethodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.EXTERNAL_BALANCE_ADJUSTMENT_ACCOUNT_METHOD,
    FieldStateKey.EXTERNAL_BALANCE_ADJUSTMENT_ACCOUNT_METHOD,
  );
  const balanceAdjustmentHoldPeriodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.BALANCE_ADJUSTMENT_HOLD_PERIOD,
    FieldStateKey.BALANCE_ADJUSTMENT_HOLD_PERIOD,
  );
  const balanceAdjustmentGiveUpPeriodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.BALANCE_ADJUSTMENT_PUSH_GIVE_UP_DAYS,
    FieldStateKey.BALANCE_ADJUSTMENT_PUSH_GIVE_UP_DAYS,
  );
  const { viewMode } = useSetupEmployerMode();

  const methodsOptions = useMemo(() => {
    const options = methods.map((option) => ({
      label: option.description,
      value: option.name,
    }));

    return options;
  }, [methods]);

  const getDefaultValue = useCallback((value?: string) => {
    const bankId = banks.find((bank) => bank.id === value)?.id;

    if (bankId) return bankId;

    if (formattedData?.normalProperties?.externalAccountEnable
      || value === NON_ASSIGNABLE_VALUE) return NON_ASSIGNABLE_VALUE;
    return '';
  }, [banks, formattedData]);

  const externalBalanceAdjustmentBank = useMemo(
    () => currentValue?.balanceAdjustmentBank,
    [currentValue],
  );

  const options = banks.map((bank) => ({
    key: bank.id,
    value: bank.description,
  }));

  const fields = useMemo(() => {
    if (isLoading) return [];

    return [
      {
        name: 'balanceAdjustmentMethod',
        type: FieldTypes.Radio,
        disabled: viewMode,
        parentValueFn: (val: string) => !!val,
        label: 'Method',
        validator: yup.string().test({
          test: (val) => !!(val || formattedData?.overridableProperties?.externalBalanceAdjustmentAccountMethod),
          message: REQUIRED_TEXT,
        }),
        options: methodsOptions,
        value: formattedData?.overridableProperties?.externalBalanceAdjustmentAccountMethod,
        showRequireIcon: true,
        ...externalBalanceAdjustmentAccountMethodPermProps,
      },
      ...(currentValue?.balanceAdjustmentMethod === AccountMethods.PULL_FUNDS ? [{
        name: 'balanceAdjustmentBank',
        type: FieldTypes.Dropdown,
        disabled: viewMode,
        showRequireIcon: true,
        validator: VALIDATORS.REQUIRED_STRING,
        areSubFieldsShouldHide: !externalBalanceAdjustmentBank || externalBalanceAdjustmentBank === NON_ASSIGNABLE_VALUE,
        placeholder,
        singleMode: true,
        label: 'Bank Account',
        options,
        defaultValue: getDefaultValue(
          formattedData?.normalProperties?.externalBalanceAdjustmentAccount,
        ),
        value: getDefaultValue(
          formattedData?.normalProperties?.externalBalanceAdjustmentAccount,
        ),
      },
      ] : [
        {
          name: 'accountToPushTo',
          type: FieldTypes.Node,
          label: 'Account to push to',
          value: (
            <AccountToPushToField
              accountNumber={hsaClearingAccount}
              routingNumber={routingNumber}
              testId="balance-adjustment"
              accountPlaceholder={ACCOUNT_PLACEHOLDER}
            />
          ),
          ...externalBalanceAdjustmentAccountMethodPermProps,
        },
      ]),
      ...(currentValue?.balanceAdjustmentMethod === AccountMethods.PUSH_FUNDS ? [
        {
          name: 'balanceAdjustmentPushGiveUpDays',
          type: FieldTypes.Mask,
          disabled: viewMode,
          label: (
            <Box flex={{ grow: 1 }}>
              <Inscription margin={{ bottom: '4px' }} weight="bold" color="textBody">
                Give up period (days)
              </Inscription>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small">
                  Failure to receive funds after the give up period has passed will result in the balance adjustment being reversed from the participants account.
                </Inscription>
              </Box>
            </Box>
          ),
          placeholder: 'Enter # of days',
          fill: false,
          value: `${formattedData?.overridableProperties?.balanceAdjustmentPushGiveUpDays}` || defaultGiveUpDays,
          ...balanceAdjustmentGiveUpPeriodPermProps,
          hasHorizontalLine: true,
          mask: '99',
        }] : [
        {
          name: 'balanceAdjustmentHoldPeriod',
          type: FieldTypes.Mask,
          disabled: viewMode,
          label: (
            <Box flex={{ grow: 1 }}>
              <Inscription margin={{ bottom: '4px' }} weight="bold" color="textBody">
                Hold period
              </Inscription>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small">
                  Number of days balance adjustment will pend on the employee account before settling to ensure funds are available.
                </Inscription>
              </Box>
            </Box>
          ),
          placeholder: 'Enter # of days',
          fill: false,
          value: `${formattedData?.overridableProperties?.balanceAdjustmentHoldPeriod}` || defaultHoldPeriod,
          ...balanceAdjustmentHoldPeriodPermProps,
          hasHorizontalLine: true,
          mask: '99',
        }]),
    ] as Field[];
  }, [
    formattedData,
    isLoading,
    viewMode,
    externalBalanceAdjustmentAccountMethodPermProps,
    balanceAdjustmentHoldPeriodPermProps,
    balanceAdjustmentGiveUpPeriodPermProps,
    currentValue,
    hsaClearingAccount,
    methodsOptions,
    options,
    getDefaultValue,
    externalBalanceAdjustmentBank,
    routingNumber,
  ]);


  const formattedFields = useHideFields(fields);
  return formattedFields;
};

export default useBalanceAdjustmentFields;
